import { Badge, Box, Divider, IconButton, Tooltip, useTheme } from '@mui/material';
import { FilterSearch, Setting2 } from 'iconsax-react';
import type { ReactElement } from 'react';

import type { FilterByOpts } from '@web/utils/ApiClient';

import { ButtonWithPopover } from '../../../ButtonWithPopover/ButtonWithPopover';
import type { Column } from '../../TableDataModel';
import type { ReadableSet } from '../../useApiTableData';
import { ColumnConfigurationForm } from '../ColumnConfiguration/ColumnConfigurationForm';
import { ClearButton } from './ClearButton';
import { ExportButton } from './ExportButton';
import { PrintButton } from './PrintButton';
import { SaveButton } from './SaveButton';

export type PrintOpts = {
  getAllRows: () => Promise<any[]>;
  tableName: string;
};

export type ExportOpts<TRow extends { id: string }> = {
  getAllRows: () => Promise<TRow[]>;
  allFilters: FilterByOpts<TRow>[];
  title: string;
};

type Props<TRow extends { id: string }> = {
  allColumns: ReadableSet<Column<TRow>>;
  renderedColumns: ReadableSet<Column<TRow>>;
  onColumnChange: (columnID: string, isRendered: boolean) => void;
  onColumnReorder: (newColumns: Column<TRow>[]) => void;
  columnsFiltersCount?: number;
  onColumnsFilterIconClick?: () => void;
  onSave?: () => void;
  onClear?: () => void;
  printOpts?: PrintOpts;
  exportOpts?: ExportOpts<TRow>;
};

export const ToolbarActions = <TRow extends { id: string }>({
  allColumns,
  renderedColumns,
  onColumnChange,
  onColumnReorder,
  columnsFiltersCount = 0,
  onColumnsFilterIconClick,
  onSave,
  onClear,
  printOpts,
  exportOpts,
}: Props<TRow>): ReactElement => {
  const theme = useTheme();
  const hasColumnFilters = columnsFiltersCount > 0;

  return (
    <Box display='flex' gap='1rem' flexWrap='wrap'>
      {onColumnsFilterIconClick !== undefined && (
        <Tooltip title='Reset Column Filters'>
          <Badge badgeContent={columnsFiltersCount} color='primary'>
            <IconButton onClick={onColumnsFilterIconClick} disabled={!hasColumnFilters}>
              <FilterSearch
                variant='TwoTone'
                color={
                  hasColumnFilters ? theme.palette.SlabIndigo['700'] : theme.palette.SlabGray['400']
                }
              />
            </IconButton>
          </Badge>
        </Tooltip>
      )}
      <Tooltip title='Configure Columns'>
        <span>
          <ButtonWithPopover
            startIcon={<Setting2 variant='TwoTone' color={theme.palette.SlabIndigo['700']} />}
          >
            <ColumnConfigurationForm
              columns={[...allColumns]}
              renderedColumns={[...renderedColumns]}
              onColumnChange={onColumnChange}
              onOrderChange={onColumnReorder}
            />
          </ButtonWithPopover>
        </span>
      </Tooltip>
      {onSave !== undefined && onClear !== undefined && (
        <>
          <SaveButton onSave={onSave} />
          <ClearButton onClear={onClear} />
        </>
      )}
      {(printOpts !== undefined || exportOpts !== undefined) && (
        // The theme's uneven vertical Divider margins would push it off-center.
        <Divider orientation='vertical' flexItem sx={{ my: '0.5rem' }} />
      )}
      {printOpts !== undefined && (
        <PrintButton
          renderedColumns={renderedColumns}
          getAllRows={printOpts.getAllRows}
          tableName={printOpts.tableName}
        />
      )}
      {exportOpts !== undefined && (
        <ExportButton
          renderedColumns={renderedColumns}
          allFilters={exportOpts.allFilters}
          getAllRows={exportOpts.getAllRows}
          title={exportOpts.title}
        />
      )}
    </Box>
  );
};
